// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapporter & indberetning's left tile as data: the tree of reports and
 * submissions, each leaf a place the centre tile shows.
 *
 *   Rapporter
 *     Ressourcekortlægning — Forside, Sagsdata, Ressourcer
 *     Miljøkortlægning     — Forside, Sagsdata, Prøver og bilag
 *   Indberetning — bygningsaffald.dk (snart), CSV export,
 *                  Nedrivningsplan (snart), Materialeflow (flag)
 *
 * A layer over `pages.ts`: its `PAGE_DEFS` stay the page catalogue (the
 * preview, the server's sections and the readiness verdicts key on them),
 * and a leaf names the pages whose readiness it sums. A leaf lives in the URL
 * as `?page=` (the catalogue's numbers, two new ones for the leaves without a
 * page) plus `?rapport=miljoe` for the Miljøkortlægning; older `?page=`
 * values (the retired strip's) land on the nearest leaf (`leafOf`).
 *
 * Pure: no React, no DOM.
 */

import type { Tone } from '../kortlaegning/vocab';
import { INCOMPLETE_TEXT, READY_TEXT, type PageId, type PageModel } from './pages';
import { INDBERETNING_PAGE } from './selection';

export type ReportId = 'ressource' | 'miljoe';
export type GroupId = ReportId | 'indberetning';

export type LeafId =
  | 'ressource.forside'
  | 'ressource.sagsdata'
  | 'ressource.ressourcer'
  | 'miljoe.forside'
  | 'miljoe.sagsdata'
  | 'miljoe.bilag'
  | 'indberetning.bygningsaffald'
  | 'indberetning.csv'
  | 'indberetning.nedrivningsplan'
  | 'indberetning.materialeflow';

/**
 * What the centre tile shows: a report page, the CSV export, "Kommer snart",
 * or the Materialeflow over the pkt. 10 fractions.
 */
export type LeafKind = 'document' | 'csv' | 'soon' | 'materialeflow';

export interface LeafDef {
  id: LeafId;
  group: GroupId;
  title: string;
  kind: LeafKind;
  /** `?page=`. */
  page: number;
  /** `?rapport=`; null = left out (the Ressourcekortlægning, and every Indberetning leaf). */
  rapport: ReportId | null;
  /** The `PAGE_DEFS` pages whose readiness the leaf sums; empty = no status. */
  pages: readonly PageId[];
  /** Not built yet: "Kommer snart" in the centre. */
  soon: boolean;
}

export type SectionId = 'rapporter' | 'indberetning';

export interface GroupDef {
  id: GroupId;
  title: string;
  section: SectionId;
  /** A report folds (`<details>`); Indberetning is a plain list under its caption. */
  collapsible: boolean;
}

/** `?page=7`: the CSV export. */
export const CSV_PAGE = 7;
/** `?page=8`: bygningsaffald.dk. */
export const BYGNINGSAFFALD_PAGE = 8;

export const SECTION_TITLE: Record<SectionId, string> = {
  rapporter: 'Rapporter',
  indberetning: 'Indberetning',
};

export const REPORT_TITLE: Record<ReportId, string> = {
  ressource: 'Ressourcekortlægning',
  miljoe: 'Miljøkortlægning',
};

/** The Miljøkortlægning's Versioner: it is part of the one generated PDF for now. */
export const MILJOE_NOTE = 'Ikke genereret separat endnu';

export const SOON_TAG = 'Snart';
export const SOON_TITLE = 'Kommer snart';

/** What a "Kommer snart" leaf will hold. */
export const SOON_DETAIL: Partial<Record<LeafId, string>> = {
  'indberetning.bygningsaffald':
    'Her kommer indberetningen til bygningsaffald.dk: Del 1 med ansøgningen om nedrivningstilladelse, Del 2 før nedrivningen går i gang.',
  'indberetning.nedrivningsplan':
    'Her kommer nedrivningsplanen: rækkefølgen for miljøsanering og nedtagning, og hvor affaldet sendes hen.',
};

export const GROUP_DEFS: readonly GroupDef[] = [
  { id: 'ressource', title: REPORT_TITLE.ressource, section: 'rapporter', collapsible: true },
  { id: 'miljoe', title: REPORT_TITLE.miljoe, section: 'rapporter', collapsible: true },
  { id: 'indberetning', title: SECTION_TITLE.indberetning, section: 'indberetning', collapsible: false },
];

/** Every leaf, in tree order (also J/K's order). */
const LEAF_DEFS: readonly LeafDef[] = [
  { id: 'ressource.forside', group: 'ressource', title: 'Forside', kind: 'document', page: 0, rapport: null, pages: ['forside'], soon: false },
  { id: 'ressource.sagsdata', group: 'ressource', title: 'Sagsdata', kind: 'document', page: 1, rapport: null, pages: ['sagsdata'], soon: false },
  { id: 'ressource.ressourcer', group: 'ressource', title: 'Ressourcer', kind: 'document', page: 2, rapport: null, pages: ['ressourcer'], soon: false },
  // The Miljøkortlægning's Forside and Sagsdata rest on the same rows as the Ressourcekortlægning's.
  { id: 'miljoe.forside', group: 'miljoe', title: 'Forside', kind: 'document', page: 0, rapport: 'miljoe', pages: ['forside'], soon: false },
  { id: 'miljoe.sagsdata', group: 'miljoe', title: 'Sagsdata', kind: 'document', page: 1, rapport: 'miljoe', pages: ['sagsdata'], soon: false },
  { id: 'miljoe.bilag', group: 'miljoe', title: 'Prøver og bilag', kind: 'document', page: 3, rapport: 'miljoe', pages: ['miljoe', 'bilag'], soon: false },
  {
    id: 'indberetning.bygningsaffald',
    group: 'indberetning',
    title: 'bygningsaffald.dk',
    kind: 'soon',
    page: BYGNINGSAFFALD_PAGE,
    rapport: null,
    pages: [],
    soon: true,
  },
  { id: 'indberetning.csv', group: 'indberetning', title: 'CSV export', kind: 'csv', page: CSV_PAGE, rapport: null, pages: [], soon: false },
  { id: 'indberetning.nedrivningsplan', group: 'indberetning', title: 'Nedrivningsplan', kind: 'soon', page: 5, rapport: null, pages: [], soon: true },
  {
    id: 'indberetning.materialeflow',
    group: 'indberetning',
    title: 'Materialeflow',
    kind: 'materialeflow',
    page: INDBERETNING_PAGE,
    rapport: null,
    pages: ['indberetning'],
    soon: false,
  },
];

/**
 * The retired strip's `?page=` values with no leaf of their own: 3
 * Miljøkortlægning and 4 Prøver & bilag (without `?rapport=`), 6 the stored
 * PDF (the versions in the right tile open it). Its 5, Nedrivningsplan, is
 * that leaf's page still.
 */
const LEGACY_PAGES: Readonly<Record<number, LeafId>> = {
  3: 'miljoe.bilag',
  4: 'miljoe.bilag',
  6: 'ressource.forside',
};

export interface TreeFlags {
  /** The Materialeflow leaf (`FeatureFlags.submission`). */
  materialeflow: boolean;
}

function leafDef(id: LeafId): LeafDef {
  // LEAF_DEFS covers every LeafId; the fallback only satisfies the type checker.
  return LEAF_DEFS.find((l) => l.id === id) ?? LEAF_DEFS[0];
}

/** The leaves the tree shows, in tree order (J/K's order). */
export function treeLeaves(flags: TreeFlags): LeafDef[] {
  return LEAF_DEFS.filter((l) => l.kind !== 'materialeflow' || flags.materialeflow);
}

/**
 * The leaf a `?page=` + `?rapport=` pair shows. An Indberetning leaf is its
 * page whatever the report; a report's leaf needs its report. Indberetning's
 * old page is the Materialeflow, or bygningsaffald.dk while that leaf is
 * hidden; the retired strip's pages land on the nearest leaf; anything else
 * on the report's Forside.
 */
export function leafOf(page: number, rapport: ReportId | null, flags: TreeFlags): LeafDef {
  const report: ReportId = rapport ?? 'ressource';
  const own = treeLeaves(flags).find((l) => l.page === page && (l.group === 'indberetning' || l.group === report));
  if (own) return own;
  if (page === INDBERETNING_PAGE) return leafDef('indberetning.bygningsaffald');
  const legacy = LEGACY_PAGES[page];
  if (legacy) return leafDef(legacy);
  return leafDef(report === 'miljoe' ? 'miljoe.forside' : 'ressource.forside');
}

/** The leaf a server section belongs to (paging across the preview); null for a section with no leaf. */
export function leafOfSection(id: PageId): LeafId | null {
  switch (id) {
    case 'forside':
      return 'ressource.forside';
    case 'sagsdata':
      return 'ressource.sagsdata';
    case 'ressourcer':
      return 'ressource.ressourcer';
    case 'miljoe':
    case 'bilag':
      return 'miljoe.bilag';
    default:
      return null;
  }
}

/** The selection patch that shows a leaf. */
export function leafPatch(id: LeafId): { page: number; rapport: ReportId | null } {
  const def = leafDef(id);
  return { page: def.page, rapport: def.rapport };
}

/** The report the right tile speaks of: an Indberetning leaf's is the Ressourcekortlægning. */
export function reportOfLeaf(id: LeafId): ReportId {
  return leafDef(id).group === 'miljoe' ? 'miljoe' : 'ressource';
}

// --------------------------------------------------------- the tree --

export type LeafStatus = 'ready' | 'incomplete' | 'soon' | null;

export interface LeafModel {
  def: LeafDef;
  status: LeafStatus;
  /** Why it is not ready (the pages' reasons, once each); empty otherwise. */
  reasons: string[];
  active: boolean;
}

export interface GroupModel {
  def: GroupDef;
  leaves: LeafModel[];
  /** A report's summary: "Klar" or "2 af 3 klar"; null for Indberetning or a group with no status. */
  rollup: { text: string; tone: Tone } | null;
}

function pagesOf(pages: readonly PageModel[], def: LeafDef): PageModel[] {
  return def.pages.flatMap((id) => pages.filter((p) => p.id === id));
}

function leafModel(def: LeafDef, pages: readonly PageModel[], active: LeafId): LeafModel {
  const base = { def, active: def.id === active };
  if (def.soon) return { ...base, status: 'soon', reasons: [] };
  const own = pagesOf(pages, def);
  if (own.length === 0) return { ...base, status: null, reasons: [] };
  const ready = own.every((p) => p.ready);
  return { ...base, status: ready ? 'ready' : 'incomplete', reasons: ready ? [] : [...new Set(own.flatMap((p) => p.reasons))] };
}

function rollupOf(def: GroupDef, leaves: readonly LeafModel[]): GroupModel['rollup'] {
  if (!def.collapsible) return null;
  const judged = leaves.filter((l) => l.status === 'ready' || l.status === 'incomplete');
  if (judged.length === 0) return null;
  const ready = judged.filter((l) => l.status === 'ready').length;
  return ready === judged.length
    ? { text: READY_TEXT, tone: 'good' }
    : { text: `${ready} af ${judged.length} klar`, tone: 'warn' };
}

/** The tree: each group's leaves with their status (from the page models), the active leaf marked. */
export function treeModel(pages: readonly PageModel[], flags: TreeFlags, active: LeafId): GroupModel[] {
  const leaves = treeLeaves(flags);
  return GROUP_DEFS.map((def) => {
    const models = leaves.filter((l) => l.group === def.id).map((l) => leafModel(l, pages, active));
    return { def, leaves: models, rollup: rollupOf(def, models) };
  });
}

/**
 * The page model the centre tile previews for a leaf: its first page's, with
 * the readiness of all its pages (Prøver og bilag = Miljøkortlægning + Prøver
 * & bilag). Null for a leaf with no page.
 */
export function leafPageModel(pages: readonly PageModel[], def: LeafDef): PageModel | null {
  const own = pagesOf(pages, def);
  const first = own[0];
  if (!first) return null;
  if (own.length === 1) return first;
  const ready = own.every((p) => p.ready);
  return {
    ...first,
    title: def.title,
    ready,
    reasons: ready ? [] : [...new Set(own.flatMap((p) => p.reasons))],
    figure: ready ? READY_TEXT : first.figure,
  };
}

/** A leaf's status as its pill reads. */
export function leafStatusText(status: LeafStatus): string | null {
  if (status === 'ready') return READY_TEXT;
  if (status === 'incomplete') return INCOMPLETE_TEXT;
  if (status === 'soon') return SOON_TAG;
  return null;
}
